import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import foodsService, {
  GetManagementFoodsParams,
} from '~/services/foodsService/foods.service';
import { ManageFoodRequestDto } from '@bitecritic-api/src/management/foods/dto/manage-food.request.dto';
import { statutesEnum } from '@bitecritic-api/src/statuses/statutes.enum';

export const MANAGEMENT_FOODS_QUERY_KEY = 'management-foods' as const;

export type UseManagementFoodsOptions = Partial<GetManagementFoodsParams> & {
  enabled?: boolean;
};

// Hook for fetching paginated foods list with management-specific data
export function useManagementFoodsList(
  options: UseManagementFoodsOptions = {},
) {
  const router = useRouter();
  const { page, search, categoryId, status, sort, limit } = router.query;

  const queryParams: GetManagementFoodsParams = {
    page: options.page || (page ? Number(page) : 1),
    search: options.search || (search as string) || '',
    categoryId:
      options.categoryId || (categoryId ? Number(categoryId) : undefined),
    status:
      options.status ||
      (status as (typeof statutesEnum)[keyof typeof statutesEnum]),
    sort: options.sort || (sort as GetManagementFoodsParams['sort']),
    limit: options.limit || (limit ? Number(limit) : 10),
  };

  const foodsQuery = useQuery({
    queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'list', queryParams],
    queryFn: () => foodsService.getManagementFoods(queryParams),
    enabled: options.enabled !== false,
  });

  const handlePageChange = useCallback(
    (newPage: number) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, page: newPage },
      });
    },
    [router],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, search: searchTerm, page: 1 },
      });
    },
    [router],
  );

  const handleStatusChange = useCallback(
    (status: (typeof statutesEnum)[keyof typeof statutesEnum] | undefined) => {
      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          status: status || undefined,
          page: 1,
        },
      });
    },
    [router],
  );

  const handleSortChange = useCallback(
    (sort: GetManagementFoodsParams['sort']) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, sort, page: 1 },
      });
    },
    [router],
  );

  const handleCategoryChange = useCallback(
    (categoryId: number | undefined) => {
      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          categoryId: categoryId || undefined,
          page: 1,
        },
      });
    },
    [router],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, limit: newLimit, page: 1 },
      });
    },
    [router],
  );

  return {
    foods: foodsQuery.data?.data ?? [],
    total: foodsQuery.data?.total ?? 0,
    currentPage: foodsQuery.data?.page ?? 1,
    totalPages: foodsQuery.data?.totalPages ?? 1,
    hasMore: foodsQuery.data?.hasMore ?? false,
    isLoading: foodsQuery.isLoading,
    error: foodsQuery.error,
    refetch: foodsQuery.refetch,
    handlePageChange,
    handleSearch,
    handleStatusChange,
    handleSortChange,
    handleCategoryChange,
    handleLimitChange,
  };
}

// Hook for fetching a single food with all details
export function useManagementFoodDetails(id: number | undefined) {
  return useQuery({
    queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'details', id],
    queryFn: () => foodsService.getManagementFoodById(id as number),
    enabled: !!id,
  });
}

// Create food mutation
export function useCreateFood() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ManageFoodRequestDto) => foodsService.createFood(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'list'],
      });
    },
  });
}

// Update food mutation
export function useUpdateFood() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ManageFoodRequestDto }) =>
      foodsService.updateFood(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'details', variables.id],
      });
    },
  });
}

// Delete food mutation
export function useDeleteFood() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => foodsService.deleteFood(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'list'],
      });
    },
  });
}

// Update food status mutation
export function useUpdateFoodStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: number;
      status: (typeof statutesEnum)[keyof typeof statutesEnum];
    }) => foodsService.updateFoodStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: [MANAGEMENT_FOODS_QUERY_KEY, 'details', variables.id],
      });
    },
  });
}
